import React from 'react';
import classNames from 'classnames';
import Fullscreen from '@mui/icons-material/Fullscreen';
import FullscreenExit from '@mui/icons-material/FullscreenExit';
import GetAppOutlined from '@mui/icons-material/GetAppOutlined';
import { Tooltip } from '@mui/material';
import { canBeFullScreen } from '../helper';
import {
  DocumentViewerActionsProps,
  DocumentViewerProps,
} from './DocumentActionsProps';
import { useFullScreenStyles } from '../useFullScreenStyles';
import ThemableIconButton from '../../../theme/components/Button/ThemableIconButton';
import useAppMode from '../../../lib/hook/config/useAppMode';
import useDarkTheme from '../../../theme/styles/useDarkTheme';

type Props = DocumentViewerProps & DocumentViewerActionsProps;

const DocumentViewerActions: React.FC<Props> = (props: Props) => {
  const fullScreenClasses = useFullScreenStyles();
  const darkThemeClasses = useDarkTheme();
  const { isEmbeddedMode } = useAppMode();

  const {
    darkTheme = false,
    disabled = false,
    // We don't use document.downloadable because it depends on
    // the context, for example members can always download the
    // document from the preview or content overview page
    downloadable,
    fullScreenHandle,
    onDownload,
  } = props;

  const renderDownloadIconButton = (): JSX.Element => {
    const isDisabled = disabled || isEmbeddedMode;

    return (
      <ThemableIconButton
        aria-label="Download"
        className={classNames({
          [fullScreenClasses.fullScreenIcon]: fullScreenHandle.active,
          [darkThemeClasses.primaryTextLight]: darkTheme,
          [darkThemeClasses.disabledTextLight]: darkTheme && isDisabled,
        })}
        disabled={isDisabled}
        onClick={onDownload}
        size="small"
        title="Download"
      >
        <GetAppOutlined />
      </ThemableIconButton>
    );
  };

  return (
    <>
      {downloadable &&
        (isEmbeddedMode ? (
          <Tooltip
            placement="top"
            PopperProps={{ disablePortal: true }}
            title="Downloads are only enabled in the room link"
          >
            <a>{renderDownloadIconButton()}</a>
          </Tooltip>
        ) : (
          renderDownloadIconButton()
        ))}
      {fullScreenHandle.active && (
        <ThemableIconButton
          aria-label="fullscreen-on"
          className={classNames({
            [fullScreenClasses.fullScreenIcon]: fullScreenHandle.active,
            [darkThemeClasses.primaryTextLight]: darkTheme,
            [darkThemeClasses.disabledTextLight]: darkTheme && disabled,
          })}
          disabled={disabled}
          onClick={fullScreenHandle.exit}
          size="small"
          title="Exit fullscreen"
        >
          <FullscreenExit />
        </ThemableIconButton>
      )}
      {!fullScreenHandle.active && canBeFullScreen() && (
        <ThemableIconButton
          aria-label="fullscreen-off"
          className={classNames({
            [fullScreenClasses.fullScreenIcon]: fullScreenHandle.active,
            [darkThemeClasses.primaryTextLight]: darkTheme,
            [darkThemeClasses.disabledTextLight]: darkTheme && disabled,
          })}
          disabled={disabled}
          onClick={fullScreenHandle.enter}
          size="small"
          title="Fullscreen"
        >
          <Fullscreen />
        </ThemableIconButton>
      )}
    </>
  );
};

export default DocumentViewerActions;
